import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FileText,
  Search,
  Eye,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Layers,
  BookOpen,
  FolderOpen,
  PlayCircle
} from 'lucide-react'
import { adminApi, AdminContent, HierarchyLevel, ContentStatus } from '@/api/admin'

const HIERARCHY_LEVELS: HierarchyLevel[] = ['program', 'curriculum', 'course', 'module', 'lesson']
const CONTENT_STATUSES: ContentStatus[] = ['draft', 'ready', 'published', 'archived']

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  program: '프로그램',
  curriculum: '커리큘럼',
  course: '코스',
  module: '모듈',
  lesson: '레슨'
}

const LEVEL_ICONS: Record<HierarchyLevel, React.ElementType> = {
  program: Layers,
  curriculum: BookOpen,
  course: FolderOpen,
  module: FolderOpen,
  lesson: PlayCircle
}

const STATUS_LABELS: Record<ContentStatus, string> = {
  draft: '초안',
  ready: '준비됨',
  published: '발행됨',
  archived: '보관됨'
}

const STATUS_COLORS: Record<ContentStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  ready: 'bg-yellow-100 text-yellow-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-red-100 text-red-700'
}

const LEVEL_COLORS: Record<HierarchyLevel, string> = {
  program: 'bg-purple-100 text-purple-700',
  curriculum: 'bg-blue-100 text-blue-700',
  course: 'bg-cyan-100 text-cyan-700',
  module: 'bg-teal-100 text-teal-700',
  lesson: 'bg-emerald-100 text-emerald-700'
}

interface ContentDetailModalProps {
  content: AdminContent
  onClose: () => void
}

function ContentDetailModal({ content, onClose }: ContentDetailModalProps) {
  const Icon = LEVEL_ICONS[content.hierarchyLevel as HierarchyLevel]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{content.title}</h2>
              <p className="text-sm text-slate-500">ID: {content.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status & Level */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
              {LEVEL_LABELS[content.hierarchyLevel as HierarchyLevel]}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[content.status as ContentStatus]}`}>
              {STATUS_LABELS[content.status as ContentStatus]}
            </span>
            {!content.isActive && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                비활성
              </span>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-500">Bare Title</label>
              <p className="text-slate-900">{content.bareTitle || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Notion Page ID</label>
              <p className="text-slate-900 font-mono text-xs">{content.notionPageId || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">예상 학습 시간</label>
              <p className="text-slate-900">
                {content.estimatedDurationMinutes ? `${content.estimatedDurationMinutes}분` : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">Parent ID</label>
              <p className="text-slate-900">{content.parentId || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">별점</label>
              <p className="text-slate-900">{content.starRating || '-'}</p>
            </div>
          </div>

          {/* Description */}
          {content.description && (
            <div>
              <label className="text-sm font-medium text-slate-500">설명</label>
              <p className="text-slate-900 mt-1">{content.description}</p>
            </div>
          )}

          {/* Short Description */}
          {content.shortDescription && (
            <div>
              <label className="text-sm font-medium text-slate-500">짧은 설명</label>
              <p className="text-slate-900 mt-1">{content.shortDescription}</p>
            </div>
          )}

          {/* Learning Objectives */}
          {content.learningObjectives && (
            <div>
              <label className="text-sm font-medium text-slate-500">학습 목표</label>
              <p className="text-slate-900 mt-1 whitespace-pre-wrap">{content.learningObjectives}</p>
            </div>
          )}

          {/* Concepts */}
          {content.concepts && (
            <div>
              <label className="text-sm font-medium text-slate-500">개념</label>
              <p className="text-slate-900 mt-1 whitespace-pre-wrap">{content.concepts}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="pt-4 border-t border-slate-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-slate-500">생성일</label>
                <p className="text-slate-900">{new Date(content.createdAt).toLocaleString('ko-KR')}</p>
              </div>
              <div>
                <label className="text-slate-500">수정일</label>
                <p className="text-slate-900">{new Date(content.updatedAt).toLocaleString('ko-KR')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary btn-md w-full">
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

export function ContentManagement() {
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [filterLevel, setFilterLevel] = useState<HierarchyLevel | ''>('')
  const [filterStatus, setFilterStatus] = useState<ContentStatus | ''>('')
  const [selectedContent, setSelectedContent] = useState<AdminContent | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'contents', { page, keyword, filterLevel, filterStatus }],
    queryFn: () => adminApi.getContents({
      page,
      size: 20,
      keyword: keyword || undefined,
      hierarchyLevel: filterLevel || undefined,
      status: filterStatus || undefined
    }),
    staleTime: 30 * 1000,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
  }

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">콘텐츠 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-amber-600" />
          콘텐츠 관리
        </h1>
        <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}개의 콘텐츠</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="제목, 설명 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-36"
            value={filterLevel}
            onChange={(e) => { setFilterLevel(e.target.value as HierarchyLevel | ''); setPage(0) }}
          >
            <option value="">모든 계층</option>
            {HIERARCHY_LEVELS.map((level) => (
              <option key={level} value={level}>{LEVEL_LABELS[level]}</option>
            ))}
          </select>
          <select
            className="input w-32"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as ContentStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {CONTENT_STATUSES.map((status) => (
              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary btn-md">
            검색
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-4 py-4"></th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">계층</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">제목</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">발행</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((content) => {
                const Icon = LEVEL_ICONS[content.hierarchyLevel as HierarchyLevel]
                const isExpanded = expandedRows.has(content.id)

                return (
                  <>
                    <tr key={content.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <button
                          onClick={() => toggleRow(content.id)}
                          className="p-1 hover:bg-slate-100 rounded-sm"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{content.id}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`p-1 rounded-sm ${LEVEL_COLORS[content.hierarchyLevel as HierarchyLevel]}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-medium">
                            {LEVEL_LABELS[content.hierarchyLevel as HierarchyLevel]}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-slate-900 max-w-xs truncate">{content.title}</p>
                        {content.bareTitle && (
                          <p className="text-xs text-slate-500 truncate">{content.bareTitle}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[content.status as ContentStatus]}`}>
                          {STATUS_LABELS[content.status as ContentStatus]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                          ${content.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                          {content.status === 'published' ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => setSelectedContent(content)}
                            className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                            title="상세 보기"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${content.id}-details`} className="bg-slate-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Parent ID:</span>
                              <span className="ml-2">{content.parentId || '-'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">학습 시간:</span>
                              <span className="ml-2">
                                {content.estimatedDurationMinutes ? `${content.estimatedDurationMinutes}분` : '-'}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-500">활성:</span>
                              <span className={`ml-2 ${content.isActive ? 'text-green-600' : 'text-red-600'}`}>
                                {content.isActive ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </div>
                          {content.shortDescription && (
                            <p className="mt-2 text-sm text-slate-600">{content.shortDescription}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 콘텐츠가 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedContent && (
        <ContentDetailModal
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
        />
      )}
    </div>
  )
}
